import { useEffect, useState, useMemo } from 'react';
import {
  Server, Wifi, WifiOff, AlertTriangle, Activity, CheckCircle2, Clock,
  Building2, Shield, Zap, Radio, Eye, RefreshCw, ChevronRight, Signal,
  ArrowUpRight, MonitorSmartphone, Cpu, Globe,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { useDashboardApi, LoadingSpinner, ErrorPanel, ResponsiveStyles } from '../../components/dashboard/DashboardShared';
import '../../components/ui/datatable.css';

/* ─── Real-time flat data setup to avoid faking metrics ─── */
function generateUptimeData(currentUptime: number) {
  const data = [];
  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const h = new Date(now.getTime() - i * 3600000);
    data.push({
      hora: `${h.getHours().toString().padStart(2, '0')}h`,
      uptime: currentUptime,
      eventos: 0,
    });
  }
  return data;
}

function generateLatencyData() {
  const data = [];
  for (let i = 11; i >= 0; i--) {
    data.push({
      min: `${i * 5}min`,
      latencia: 0,
    });
  }
  return data.reverse();
}

/* ─── Pulse animation dot ─── */
function PulseDot({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: color,
        animation: 'pulse 2s infinite',
      }} />
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: color, opacity: .4,
        animation: 'pulsering 2s infinite',
      }} />
    </span>
  );
}

/* ─── Status Card ─── */
function NOCCard({ icon: Icon, label, value, sub, color, pulse }: {
  icon: any; label: string; value: string | number; sub?: string; color: string; pulse?: boolean;
}) {
  return (
    <div style={{
      background: 'var(--bg-card, white)', borderRadius: 16,
      border: '1px solid var(--border-color, #e5e7eb)', padding: '18px 22px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={16} style={{ color }} />
          <span style={{ fontSize: '.72rem', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
        </div>
        {pulse && <PulseDot color={color} />}
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--text-main, #111)' }}>{value}</div>
      {sub && <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #9ca3af)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ─── School Health Bar ─── */
function SchoolHealthRow({ name, status, devices, alerts, uptime }: {
  name: string; status: 'online' | 'degraded' | 'offline';
  devices: number; alerts: number; uptime: number;
}) {
  const statusCfg = {
    online: { bg: '#f0fdf4', color: '#16a34a', label: 'Online', icon: CheckCircle2 },
    degraded: { bg: '#fffbeb', color: '#d97706', label: 'Degradado', icon: AlertTriangle },
    offline: { bg: '#fef2f2', color: '#dc2626', label: 'Offline', icon: WifiOff },
  };
  const cfg = statusCfg[status];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
      background: 'var(--bg-main, #f9fafb)', borderRadius: 12,
      borderLeft: `4px solid ${cfg.color}`, transition: 'all .15s',
    }}>
      <PulseDot color={cfg.color} size={8} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '.88rem' }}>{name}</div>
        <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #6b7280)', marginTop: 2, display: 'flex', gap: 12 }}>
          <span><MonitorSmartphone size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{devices} dispositivos</span>
          <span><AlertTriangle size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{alerts} alertas</span>
          <span><Cpu size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{uptime}% uptime</span>
        </div>
      </div>
      <span style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '4px 12px', borderRadius: 10, fontSize: '.75rem', fontWeight: 700,
        background: cfg.bg, color: cfg.color,
      }}>
        <cfg.icon size={12} /> {cfg.label}
      </span>
    </div>
  );
}

/* ─── Activity Log Entry ─── */
function LogEntry({ time, type, message, severity }: {
  time: string; type: string; message: string; severity: 'info' | 'warn' | 'ok';
}) {
  const colors = { info: '#3b82f6', warn: '#f59e0b', ok: '#10b981' };
  const color = colors[severity];
  return (
    <div style={{
      display: 'flex', gap: 12, padding: '10px 0',
      borderBottom: '1px solid var(--border-color, #f3f4f6)',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: color,
        marginTop: 8, flexShrink: 0,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            padding: '1px 8px', borderRadius: 6, fontSize: '.65rem', fontWeight: 700,
            background: `${color}15`, color, textTransform: 'uppercase',
          }}>{type}</span>
          <span style={{ fontSize: '.72rem', color: 'var(--text-muted, #9ca3af)' }}>{time}</span>
        </div>
        <div style={{ fontSize: '.82rem', color: 'var(--text-main, #374151)', marginTop: 3 }}>{message}</div>
      </div>
    </div>
  );
}

/* ─── Main ─── */
export function DashboardNOC() {
  const { data, isLoading, error } = useDashboardApi('dashboard-noc', '/noc/dashboard');
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);


  if (isLoading) return <LoadingSpinner label="Carregando NOC..." />;
  if (error) return <ErrorPanel />;

  const c = data?.cards || {};
  const totalOnline = c.totalDispositivosOnline || 0;
  const totalDispositivos = c.totalDispositivos || 0;
  const disponibilidade = c.disponibilidadeDispositivos || 0;

  // Dados reais mapeados do backend
  const feedAtividades = data?.operacao?.feed || [];
  const activityLog = feedAtividades.map((log: any) => {
    let severidade: 'info' | 'warn' | 'ok' | 'error' = 'info';
    let mensagem = `${log.escola?.nome} — ${log.tipo?.replace(/_/g, ' ')}`;
    
    if (log.status === 'VALIDO') severidade = 'ok';
    if (log.status === 'INVALIDO') {
      severidade = 'error';
      mensagem += `: ${log.mensagemErro || 'Evento inválido'}`;
    }

    if (log.aluno) {
      mensagem += ` (Aluno: ${log.aluno.nome})`;
    }

    return {
      time: new Date(log.dataHora).toLocaleTimeString('pt-BR'),
      type: log.tipo,
      message: mensagem,
      severity: severidade,
      id: log.id,
    };
  });

  const uptimeData = generateUptimeData(disponibilidade);
  const latencyData = generateLatencyData();

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ NOC HEADER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #042f2e 0%, #0f172a 50%, #1e1b4b 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        {/* Grid effect */}
        <div style={{
          position: 'absolute', inset: 0, opacity: .05,
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(16,185,129,.08)' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Radio size={18} style={{ color: '#10b981' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>
                  Network Operations Center
                </span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>
                Centro de Operações
              </h1>
              <p style={{ color: 'rgba(255,255,255,.4)', fontSize: '.82rem', marginTop: 4 }}>
                Monitoramento em tempo real de toda a infraestrutura
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '1.8rem', fontWeight: 900, color: '#10b981', letterSpacing: '.05em' }}>
                {clock.toLocaleTimeString('pt-BR')}
              </div>
              <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.3)', marginTop: 2 }}>
                {clock.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </div>

          {/* Hero Stats with Icons */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20, marginTop: 24 }}>
            {[
              { icon: Building2, value: c.totalEscolas || 0, label: 'Escolas', color: '#0891b2' },
              { icon: Wifi, value: totalOnline, label: 'Online', color: '#10b981' },
              { icon: Zap, value: '-', label: 'Latência', color: '#8b5cf6' },
              { icon: Shield, value: `${disponibilidade}%`, label: 'Disponibilidade', color: '#3b82f6' },
              { icon: Eye, value: (c.totalEventosHoje || 0).toLocaleString('pt-BR'), label: 'Eventos Hoje', color: '#f59e0b' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${s.color}20`, color: s.color, backdropFilter: 'blur(10px)',
                }}>
                  <s.icon size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* System Status Banner */}
          <div style={{
            marginTop: 20, display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 18px', borderRadius: 12,
            background: disponibilidade >= 95 ? 'rgba(16,185,129,.12)' : 'rgba(245,158,11,.12)',
            border: `1px solid ${disponibilidade >= 95 ? 'rgba(16,185,129,.2)' : 'rgba(245,158,11,.2)'}`,
          }}>
            <PulseDot color={disponibilidade >= 95 ? '#10b981' : '#f59e0b'} size={12} />
            <span style={{
              color: disponibilidade >= 95 ? '#10b981' : '#f59e0b',
              fontWeight: 700, fontSize: '.85rem',
            }}>
              {disponibilidade >= 95 ? 'Todos os sistemas operacionais' : 'Atenção: degradação parcial'}
            </span>
            <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,.3)', fontSize: '.75rem' }}>
              Disponibilidade: {disponibilidade}%
            </span>
          </div>
        </div>
      </div>

      {/* ═══ CHARTS ROW ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* Uptime 24h */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: '.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <Activity size={18} style={{ color: '#10b981' }} />
              Uptime & Eventos (24h)
            </h3>
            <span style={{
              padding: '3px 10px', borderRadius: 8, fontSize: '.7rem', fontWeight: 700,
              background: '#10b98115', color: '#10b981', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <ArrowUpRight size={11} /> 99.8% média
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={uptimeData}>
              <defs>
                <linearGradient id="uptimeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
              <XAxis dataKey="hora" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis domain={[95, 100]} tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `${v}%`} />
              <Tooltip
                formatter={(v: any, name: any) => [name === 'uptime' ? `${Number(v).toFixed(1)}%` : v, name === 'uptime' ? 'Uptime' : 'Eventos']}
                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.8rem' }}
              />
              <Area type="monotone" dataKey="uptime" stroke="#10b981" strokeWidth={2} fill="url(#uptimeGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Latency Chart */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: '.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <Zap size={18} style={{ color: '#8b5cf6' }} />
              Latência API
            </h3>
            <span style={{ fontSize: '.72rem', color: 'var(--text-muted, #9ca3af)' }}>último 1h</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={latencyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
              <XAxis dataKey="min" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `${v}ms`} />
              <Tooltip
                formatter={(v: any) => [`${v}ms`, 'Latência']}
                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.8rem' }}
              />
              <Line type="monotone" dataKey="latencia" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ═══ STATUS ROW ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* School Health */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server size={18} style={{ color: '#0891b2' }} />
            Saúde dos Nós (Escolas)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data?.operacao?.statusEscolas || []).length > 0 ? (
              data.operacao.statusEscolas.map((r: any) => (
                <SchoolHealthRow
                  key={r.escola.id}
                  name={r.escola.nome}
                  status={r.statusSaude === 'normal' ? 'online' : r.statusSaude === 'atencao' ? 'degraded' : 'offline'}
                  devices={r.dispositivosProblema || 0}
                  alerts={r.alertasAtivos || 0}
                  uptime={r.statusSaude === 'normal' ? 100 : r.statusSaude === 'atencao' ? 98.5 : 0}
                />
              ))
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted, #9ca3af)', padding: '20px 0' }}>
                Nenhuma escola conectada à rede.
              </div>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: '.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <Globe size={18} style={{ color: '#6366f1' }} />
              Log de Atividades
            </h3>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 8, background: 'var(--bg-main, #f3f4f6)',
              fontSize: '.72rem', color: 'var(--text-muted, #6b7280)',
            }}>
              <RefreshCw size={11} /> Tempo real
            </div>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {activityLog.length > 0 ? (
              activityLog.map((log: any) => (
                <LogEntry key={log.id} {...log} />
              ))
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted, #9ca3af)', padding: '30px 0' }}>
                Nenhum evento registrado recentemente.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ INFRA BADGES ═══ */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: 16,
        border: '1px solid var(--border-color, #e5e7eb)', padding: '20px 24px',
      }}>
        <h3 style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Cpu size={18} style={{ color: '#6b7280' }} />
          Infraestrutura
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {[
            { label: 'Cloud Run', status: 'online', icon: '☁️' },
            { label: 'Cloud SQL', status: 'online', icon: '🗄️' },
            { label: 'Redis', status: 'online', icon: '⚡' },
            { label: 'Firebase Hosting', status: 'online', icon: '🌐' },
            { label: 'FCM Push', status: 'online', icon: '📱' },
            { label: 'RFID Gateway', status: 'online', icon: '📡' },
            { label: 'Backup Auto', status: 'online', icon: '💾' },
            { label: 'SSL/TLS', status: 'online', icon: '🔒' },
          ].map(svc => (
            <div key={svc.label} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
              background: 'var(--bg-main, #f9fafb)', borderRadius: 10,
              border: '1px solid var(--border-color, #e5e7eb)',
            }}>
              <span>{svc.icon}</span>
              <span style={{ fontSize: '.82rem', fontWeight: 600 }}>{svc.label}</span>
              <PulseDot color="#10b981" size={6} />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
        @keyframes pulsering { 0% { transform: scale(1); opacity: .4; } 100% { transform: scale(2.5); opacity: 0; } }
        .animate-fadeIn { animation: fadeIn .4s ease; }
        @media (max-width: 1024px) {
          div[style*="repeat(5, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          div[style*="2fr 1fr"] { grid-template-columns: 1fr !important; }
          div[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
